// ============================================================
// Stockfish WASM Integration — Task 1.2
// Loads Stockfish WASM in a Web Worker, evaluates each position
// via UCI protocol, reports progress.
// ============================================================

import type { MoveData } from "@shared/types"

// --- Types ---

export interface EvalResult {
  /** Centipawn evaluation from White's perspective */
  score: number
  /** Best move in UCI format (e.g., "e2e4") — null if engine didn't find one */
  bestMoveUci: string | null
  /** Best move in SAN format — null if not resolved */
  bestMoveSan: string | null
  /** Depth reached */
  depth: number
}

export interface StockfishProgress {
  currentMove: number
  totalMoves: number
  phase: "loading" | "analyzing" | "done"
}

export type ProgressCallback = (progress: StockfishProgress) => void

// --- Constants ---

const DEFAULT_DEPTH = 16
const EVAL_TIMEOUT_MS = 15_000
const MATE_SCORE = 10_000

// --- UCI message parsing ---

interface UciInfo {
  depth?: number
  score?: number
  isMate?: boolean
  mateIn?: number
  pv?: string[]
}

function parseInfoLine(line: string): UciInfo | null {
  if (!line.startsWith("info")) return null

  const info: UciInfo = {}
  const tokens = line.split(" ")

  for (let i = 0; i < tokens.length; i++) {
    switch (tokens[i]) {
      case "depth":
        info.depth = parseInt(tokens[i + 1], 10)
        break
      case "score":
        if (tokens[i + 1] === "cp") {
          info.score = parseInt(tokens[i + 2], 10)
          info.isMate = false
        } else if (tokens[i + 1] === "mate") {
          info.mateIn = parseInt(tokens[i + 2], 10)
          info.isMate = true
          // Convert mate score to centipawns (large value with sign)
          info.score =
            info.mateIn > 0
              ? MATE_SCORE - Math.abs(info.mateIn)
              : -(MATE_SCORE - Math.abs(info.mateIn))
        }
        break
      case "pv":
        info.pv = tokens.slice(i + 1)
        break
    }
  }

  return info
}

function parseBestMove(line: string): string | null {
  if (!line.startsWith("bestmove")) return null
  const tokens = line.split(" ")
  return tokens[1] ?? null
}

// --- Stockfish Engine Class ---

export class StockfishEngine {
  private worker: Worker | null = null
  private ready = false
  private messageBuffer: string[] = []
  private resolveReady: (() => void) | null = null

  /**
   * Initialize the Stockfish WASM engine.
   * Loads the lite single-threaded version (~7MB) for broad compatibility.
   */
  async init(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        // The stockfish package provides a self-contained JS file that works
        // as a Web Worker. It expects to find the .wasm file relative to itself.
        // We need to serve these files from the public directory.
        const wasmPath = new URL(
          "/stockfish/stockfish-18-lite-single.js",
          window.location.origin
        ).href

        this.worker = new Worker(wasmPath)

        this.resolveReady = resolve

        this.worker.onmessage = (e: MessageEvent) => {
          const line = typeof e.data === "string" ? e.data : String(e.data)
          this.handleMessage(line)
        }

        this.worker.onerror = (e: ErrorEvent) => {
          reject(new Error(`Stockfish worker error: ${e.message}`))
        }

        // Send UCI init command
        this.send("uci")
      } catch (err) {
        reject(err)
      }
    })
  }

  private handleMessage(line: string): void {
    this.messageBuffer.push(line)

    // Handle UCI initialization
    if (line === "uciok" && !this.ready) {
      this.ready = true
      // Configure engine for analysis
      this.send(`setoption name Hash value 16`)
      this.send("isready")
    }

    if (line === "readyok" && this.resolveReady) {
      const resolve = this.resolveReady
      this.resolveReady = null
      resolve()
    }
  }

  private send(command: string): void {
    if (!this.worker) throw new Error("Stockfish not initialized")
    this.worker.postMessage(command)
  }

  /**
   * Evaluate a single position.
   * Returns centipawn eval from White's perspective.
   */
  async evaluatePosition(
    fen: string,
    depth: number = DEFAULT_DEPTH
  ): Promise<EvalResult> {
    return new Promise<EvalResult>((resolve, reject) => {
      if (!this.worker) {
        reject(new Error("Stockfish not initialized"))
        return
      }

      let lastInfo: UciInfo = {}
      let bestMoveUci: string | null = null
      let settled = false

      const timeout = setTimeout(() => {
        if (!settled) {
          settled = true
          // Return whatever we have so far
          resolve({
            score: lastInfo.score ?? 0,
            bestMoveUci: lastInfo.pv?.[0] ?? null,
            bestMoveSan: null,
            depth: lastInfo.depth ?? 0,
          })
        }
      }, EVAL_TIMEOUT_MS)

      const originalHandler = this.worker.onmessage

      this.worker.onmessage = (e: MessageEvent) => {
        const line = typeof e.data === "string" ? e.data : String(e.data)

        const info = parseInfoLine(line)
        if (info && info.depth !== undefined) {
          lastInfo = info
        }

        const bm = parseBestMove(line)
        if (bm !== null) {
          bestMoveUci = bm
          clearTimeout(timeout)
          if (!settled) {
            settled = true
            this.worker!.onmessage = originalHandler
            resolve({
              score: lastInfo.score ?? 0,
              bestMoveUci,
              bestMoveSan: null,
              depth: lastInfo.depth ?? depth,
            })
          }
        }
      }

      this.send(`position fen ${fen}`)
      this.send(`go depth ${depth}`)
    })
  }

  /**
   * Evaluate all positions in a game.
   * Takes moves from the PGN parser (without eval data) and returns
   * complete MoveData with eval scores added.
   */
  async evaluateGame(
    moves: Omit<MoveData, "eval" | "bestMove" | "evalDelta">[],
    startingFen: string,
    onProgress?: ProgressCallback,
    depth: number = DEFAULT_DEPTH
  ): Promise<MoveData[]> {
    const totalMoves = moves.length
    const evaluatedMoves: MoveData[] = []

    onProgress?.({ currentMove: 0, totalMoves, phase: "analyzing" })

    // Evaluate the starting position to get a baseline.
    // Normalize to White's perspective (starting FEN is usually White to move,
    // but handle edge cases where a game starts from Black's turn).
    const startingEval = await this.evaluatePosition(startingFen, depth)
    const isBlackToMoveFirst = startingFen.includes(" b ")
    let prevScore = isBlackToMoveFirst ? -startingEval.score : startingEval.score

    for (let i = 0; i < moves.length; i++) {
      const move = moves[i]

      // Evaluate the position AFTER the move
      const result = await this.evaluatePosition(move.fen, depth)

      // Stockfish returns eval from the side-to-move's perspective.
      // After White moves → Black to move → negate to normalize to White's view.
      // After Black moves → White to move → score is already White's view.
      const evalScore = move.color === "white" ? -result.score : result.score

      // evalDelta: how much the eval changed from the previous position,
      // always from White's perspective (same as eval).
      // Positive = position improved for White. Negative = improved for Black.
      // The LLM knows which color moved, so it can interpret:
      //   White move + negative delta = White blundered
      //   Black move + positive delta = Black blundered (White gained)
      const evalDelta = evalScore - prevScore

      // If engine found a better move, include it
      const bestMove =
        result.bestMoveUci && result.bestMoveUci !== `${move.from}${move.to}`
          ? result.bestMoveSan
          : null

      evaluatedMoves.push({
        ...move,
        eval: evalScore,
        bestMove,
        evalDelta,
      })

      prevScore = evalScore

      onProgress?.({
        currentMove: i + 1,
        totalMoves,
        phase: "analyzing",
      })
    }

    onProgress?.({ currentMove: totalMoves, totalMoves, phase: "done" })

    return evaluatedMoves
  }

  /**
   * Clean up the engine worker.
   */
  destroy(): void {
    if (this.worker) {
      this.send("quit")
      this.worker.terminate()
      this.worker = null
      this.ready = false
    }
  }
}

// --- Convenience function for the pipeline ---

/**
 * Analyze a full game's positions with Stockfish.
 * Creates an engine, evaluates all positions, then cleans up.
 */
export async function analyzeGame(
  moves: Omit<MoveData, "eval" | "bestMove" | "evalDelta">[],
  startingFen: string,
  onProgress?: ProgressCallback,
  depth?: number
): Promise<MoveData[]> {
  const engine = new StockfishEngine()
  try {
    await engine.init()
    return await engine.evaluateGame(moves, startingFen, onProgress, depth)
  } finally {
    engine.destroy()
  }
}
